"use client";

import type { FormEventHandler } from "react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, AlertDescription, Button, Label, LoadingButton, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Skeleton } from "@akashnetwork/ui/components";
import type { AddCreditsAmountValue } from "@src/components/billing-usage/AddCreditsAmountFields/AddCreditsAmountFields";
import { AddCreditsAmountFields } from "@src/components/billing-usage/AddCreditsAmountFields/AddCreditsAmountFields";
import { suggestedPresetAmountUsd } from "@src/components/billing-usage/AddCreditsAmountFields/presetAmounts";
import type { PaymentFormLoadError, PaymentMethodSetupFailure, PaymentMethodSetupResult, PaymentMethodSourceHandle } from "@src/components/billing-usage/AddCreditsNewPaymentMethodFields/AddCreditsNewPaymentMethodFields";
import { AddCreditsNewPaymentMethodFields } from "@src/components/billing-usage/AddCreditsNewPaymentMethodFields/AddCreditsNewPaymentMethodFields";
import { AddCreditsWalletButton, type WalletPaymentSource } from "@src/components/billing-usage/AddCreditsWalletButton/AddCreditsWalletButton";
import { FirstPurchaseBonusAlert } from "@src/components/billing-usage/FirstPurchaseBonusAlert/FirstPurchaseBonusAlert";
import { getPaymentMethodDisplay } from "@src/components/shared/PaymentMethodCard/PaymentMethodCard";
import { ThreeDSecurePopup } from "@src/components/shared/PaymentMethodForm/ThreeDSecurePopup";
import { usePaymentPolling } from "@src/context/PaymentPollingProvider";
import { useServices } from "@src/context/ServicesProvider";
import { useWallet } from "@src/context/WalletProvider";
import { use3DSecure } from "@src/hooks/use3DSecure";
import { useUser } from "@src/hooks/useUser";
import { useWalletBalance } from "@src/hooks/useWalletBalance";
import { usePaymentMethodsQuery, usePaymentMutations, useRefreshPaymentMethods, useSetupIntentMutation } from "@src/queries";
import { handleStripeError } from "@src/utils/stripeErrorHandler";
import { usePaymentRefusals } from "./usePaymentRefusals/usePaymentRefusals";
import { useTopUpAttemptKey } from "./useTopUpAttemptKey/useTopUpAttemptKey";
import { waitForSettledTransaction } from "./waitForSettledTransaction/waitForSettledTransaction";

/** Sentinel Select value for entering a new card instead of charging a saved method. */
const NEW_CARD = "new";

/** Issuer declines such as `generic_decline` and `fraudulent` share this code on setup and on charge, where the API hides the finer decline code. */
const BANK_REFUSAL_CODE = "card_declined";

/** Stripe already reports a first decline inline, so the form only steps in once the bank refuses a new card twice in a row. */
const NEW_CARD_REFUSALS_BEFORE_RESET = 2;

/** A decline such as insufficient funds can clear before a retry, so it takes a second refusal of the same payment method in the session to move off it. */
const CHARGE_DECLINES_BEFORE_RESET = 2;
const CARD_ERROR_TYPE = "card_error";
const SETUP_AUTHENTICATION_FAILURE_CODE = "setup_intent_authentication_failure";
const MS_PER_MINUTE = 60_000;
const BANK_REFUSED_MESSAGE = "Your bank refused this card. Try a different card or payment method.";
const PAYMENT_IN_PROGRESS_CODE = "payment_in_progress";
const PAYMENT_FORM_LOAD_TIMEOUT_MS = 10_000;
export const DEPENDENCIES = {
  AddCreditsAmountFields,
  AddCreditsNewPaymentMethodFields,
  AddCreditsWalletButton,
  FirstPurchaseBonusAlert,
  ThreeDSecurePopup,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  useSetupIntentMutation,
  usePaymentMethodsQuery,
  usePaymentMutations,
  usePaymentPolling,
  useRefreshPaymentMethods,
  useServices,
  useWallet,
  useWalletBalance,
  use3DSecure,
  useUser,
  useTopUpAttemptKey,
  usePaymentRefusals,
  // eslint-disable-next-line akash/dependencies-component-or-hook
  getPaymentMethodDisplay,
  // eslint-disable-next-line akash/dependencies-component-or-hook
  handleStripeError,
  // eslint-disable-next-line akash/dependencies-component-or-hook
  waitForSettledTransaction
};
interface AddCreditsFormProps {
  onDone: (amount: number, bonusAmount?: number) => void;
  onProcessingChange?: (isProcessing: boolean) => void;
  context?: string;
  deploymentDailyCostUsd?: number;
  dependencies?: typeof DEPENDENCIES;
}
interface PaymentAnalytics {
  amount: number;
  type: string;
  isSavedMethod: boolean;
  context?: string;
}
type ThreeDSecureResult = "succeeded" | "failed";
type PaymentFailureStage = "payment_method_setup" | "charge_declined" | "charge_error" | "three_d_secure" | "confirmation_timeout" | "trial_activation_timeout";
type PaymentFormLoadFailure = PaymentFormLoadError | "timeout";
interface PaymentFormLoad {
  attempt: number;
  status: "loading" | "slow" | "ready" | "failed";
}
interface PaymentFailureDetails extends PaymentMethodSetupFailure {
  httpStatus?: number;
}
interface PendingCharge {
  paymentMethodId: string;
  amount: number;
  wasTrialing: boolean;
  idempotencyKey: string;
  analytics: PaymentAnalytics;
  transactionId?: string;
}

/**
 * Orchestrates the Add Credits flow. Saved payment methods are offered when
 * the user has any (default pre-selected), with new-card entry as a fallback
 * or explicit choice. On submit it resolves the payment method (saved id, or
 * the swappable child for a new card), stores it as a pending charge and fires
 * confirmPayment once — the managed wallet may still be provisioning, in which
 * case the server replies with a retriable `wallet_provisioning` 409 that the
 * payment mutation retries with backoff until activation lands. A 409
 * `payment_in_progress` means an earlier request is still charging the same
 * attempt, so the form keeps the charge locked and polls for its settlement
 * instead of failing. It hands off to the 3D Secure popup when required,
 * then waits for payment polling to confirm settlement before notifying the caller
 * through onDone. A poll that exhausts without confirmation is NOT completion:
 * the charge may still settle, so the form releases its in-flight state
 * without onDone and the attempt stays replayable. A new card is confirmed
 * against its SetupIntent only once: retries after a failed charge reuse the
 * saved payment method instead of re-confirming a consumed intent. Each charge
 * carries a replay-safe idempotency key managed by useTopUpAttemptKey.
 */
export function AddCreditsForm({
  onDone,
  onProcessingChange,
  context,
  deploymentDailyCostUsd,
  dependencies: d = DEPENDENCIES
}: AddCreditsFormProps) {
  const {
    data: setupIntent,
    mutate: createSetupIntent,
    status: setupIntentStatus,
    reset: resetSetupIntent
  } = d.useSetupIntentMutation();
  const {
    user
  } = d.useUser();
  const {
    pollForPayment,
    isPolling,
    lastOutcome
  } = d.usePaymentPolling();
  const {
    stripe,
    analyticsService,
    api,
    queryClient
  } = d.useServices();
  const {
    isTrialing,
    topUpMinAmountUsd
  } = d.useWallet();
  const {
    balance
  } = d.useWalletBalance();
  const refreshPaymentMethods = d.useRefreshPaymentMethods();
  const {
    data: paymentMethods,
    isLoading: isLoadingMethods,
    isError: isMethodsError
  } = d.usePaymentMethodsQuery();
  const {
    confirmPayment: {
      mutateAsync: confirmPayment
    }
  } = d.usePaymentMutations();
  const attempt = d.useTopUpAttemptKey();
  const refusals = d.usePaymentRefusals(user?.id);
  const [amountInput, setAmountInput] = useState<AddCreditsAmountValue>(() => ({
    predefinedAmount: suggestedPresetAmountUsd(deploymentDailyCostUsd)?.toString(),
    customAmount: ""
  }));
  const [selectedMethodId, setSelectedMethodId] = useState<string | undefined>(undefined);
  const [charge, setCharge] = useState<PendingCharge | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorAction, setErrorAction] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isWalletOpen, setIsWalletOpen] = useState(false);
  const [walletMethod, setWalletMethod] = useState<{
    paymentMethodId: string;
    type: string;
  } | null>(null);
  const [paymentFormLoad, setPaymentFormLoad] = useState<PaymentFormLoad>({
    attempt: 0,
    status: "loading"
  });
  const submittingRef = useRef(false);
  const paymentMethodRef = useRef<PaymentMethodSourceHandle>(null);
  /** Payment method already confirmed against the current SetupIntent; reused on retry because a SetupIntent can only be confirmed once. */
  const confirmedNewCardRef = useRef<{
    paymentMethodId: string;
  } | null>(null);
  /** Last payment-method type reported to analytics, so the payment element's frequent change events don't re-fire the same type. */
  const lastPaymentTypeRef = useRef<string | null>(null);
  const wasPollingRef = useRef<boolean>(false);
  /** The charge awaiting 3D Secure, read by the 3DS callbacks which fire outside the charge flow. */
  const threeDSecureChargeRef = useRef<PendingCharge | null>(null);
  const consecutiveNewCardRefusalsRef = useRef(0);
  /** The payment element reloads with every new SetupIntent, so readiness is reported for the first load only. */
  const paymentFormReadyTrackedRef = useRef(false);
  /** Cleared by the ready callback itself, since the card form can report ready after the timeout is due but before a re-render clears it. */
  const paymentFormLoadTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [openedAt] = useState(() => performance.now());
  const amount = useMemo(() => Number(amountInput.predefinedAmount || amountInput.customAmount) || 0, [amountInput.predefinedAmount, amountInput.customAmount]);
  const amountError = amount > 0 && amount < topUpMinAmountUsd ? `Minimum amount is $${topUpMinAmountUsd}` : undefined;
  const isSetupLoading = setupIntentStatus === "pending" || setupIntentStatus === "idle";
  const isNewCard = selectedMethodId === NEW_CARD || selectedMethodId === undefined && !isLoadingMethods && !paymentMethods?.length;
  const isPaymentFormLoading = isNewCard && setupIntentStatus !== "error" && paymentFormLoad.status === "loading";
  const isPaymentFormSlow = isNewCard && setupIntentStatus !== "error" && paymentFormLoad.status === "slow";
  const hasPaymentFormFailed = isNewCard && paymentFormLoad.status === "failed";
  const isBusy = isProcessing || isWalletOpen;
  const isPaused = refusals.pausedUntil !== null;
  const pendingWallet = user?.id ? attempt.getWalletAttempt({
    userId: user.id,
    amount
  }) : null;
  const pendingWalletId = pendingWallet?.paymentMethodId;
  const pendingWalletType = pendingWallet?.type;
  useEffect(function restorePendingWallet() {
    if (!pendingWalletId || !pendingWalletType) return;
    setWalletMethod({
      paymentMethodId: pendingWalletId,
      type: pendingWalletType
    });
    setSelectedMethodId(pendingWalletId);
  }, [pendingWalletId, pendingWalletType]);
  useEffect(function selectDefaultMethodOnce() {
    if (selectedMethodId !== undefined || isLoadingMethods) return;
    const unrefusedMethods = paymentMethods?.filter(method => refusals.countFor(method.id) === 0);
    setSelectedMethodId(unrefusedMethods?.find(method => method.type === "link")?.id ?? unrefusedMethods?.find(method => method.isDefault)?.id ?? unrefusedMethods?.[0]?.id ?? NEW_CARD);
  }, [selectedMethodId, isLoadingMethods, paymentMethods, refusals]);
  useEffect(function createSetupIntentForNewCard() {
    if (isNewCard && setupIntentStatus === "idle") {
      createSetupIntent();
    }
  }, [isNewCard, setupIntentStatus, createSetupIntent]);
  const trackPaymentFailed = useCallback((analytics: PaymentAnalytics | null, stage: PaymentFailureStage, details?: PaymentFailureDetails) => {
    analyticsService.track("add_credits_payment_failed", {
      category: "billing",
      ...analytics,
      stage,
      ...details
    });
  }, [analyticsService]);
  const leadWithNewPaymentMethod = useCallback(() => {
    lastPaymentTypeRef.current = null;
    confirmedNewCardRef.current = null;
    resetSetupIntent();
    setPaymentFormLoad(restartPaymentFormLoad);
    setSelectedMethodId(NEW_CARD);
  }, [resetSetupIntent]);
  const recordNewCardSetup = (result: PaymentMethodSetupResult) => {
    if ("failure" in result && isRefusedByStripe(result.failure)) refusals.record();
    const isRefused = "failure" in result && isBankRefusal(result.failure);
    consecutiveNewCardRefusalsRef.current = isRefused ? consecutiveNewCardRefusalsRef.current + 1 : 0;
    if (consecutiveNewCardRefusalsRef.current < NEW_CARD_REFUSALS_BEFORE_RESET) return;
    consecutiveNewCardRefusalsRef.current = 0;
    leadWithNewPaymentMethod();
    setError(BANK_REFUSED_MESSAGE);
  };
  const describeSelectedPayment = (): PaymentAnalytics => {
    if (isNewCard) {
      return {
        amount,
        type: lastPaymentTypeRef.current ?? "card",
        isSavedMethod: false,
        context
      };
    }
    const savedMethod = paymentMethods?.find(method => method.id === selectedMethodId);
    const type = walletMethod && walletMethod.paymentMethodId === selectedMethodId ? walletMethod.type : toPaymentMethodType(savedMethod?.type ?? "unknown");
    return {
      amount,
      type,
      isSavedMethod: true,
      context
    };
  };
  const purchase = async (wallet?: WalletPaymentSource) => {
    if (!user?.id || amount < topUpMinAmountUsd || isProcessing || isPaused || submittingRef.current || wallet && attempt.getWalletAttempt({
      userId: user.id,
      amount
    })) {
      return;
    }
    submittingRef.current = true;
    setError(null);
    setErrorAction(null);
    setIsProcessing(true);
    const analytics: PaymentAnalytics = wallet ? {
      amount,
      type: wallet.type,
      isSavedMethod: false,
      context
    } : describeSelectedPayment();
    if (wallet) analyticsService.track("add_credits_payment_method_selected", {
      category: "billing",
      type: wallet.type
    });
    analyticsService.track("add_credits_payment_submitted", {
      category: "billing",
      ...analytics
    });
    let paymentMethodId = selectedMethodId;
    try {
      if (wallet || isNewCard) {
        let paymentMethod = wallet ? null : confirmedNewCardRef.current;
        if (!paymentMethod) {
          const setupResult: PaymentMethodSetupResult = (await (wallet ?? paymentMethodRef.current)?.addPaymentMethod()) ?? {
            failure: {
              errorType: "form_not_ready"
            }
          };
          if (!wallet) recordNewCardSetup(setupResult);
          if ("failure" in setupResult) {
            trackPaymentFailed(analytics, "payment_method_setup", setupResult.failure);
            setIsProcessing(false);
            return;
          }
          paymentMethod = setupResult;
          if (!wallet) confirmedNewCardRef.current = paymentMethod;
        }
        paymentMethodId = paymentMethod.paymentMethodId;
        if (wallet) {
          setWalletMethod({
            ...paymentMethod,
            type: wallet.type
          });
          setSelectedMethodId(paymentMethodId);
        }
      }
    } catch {
      trackPaymentFailed(analytics, "payment_method_setup");
      setError("Couldn't prepare your payment. Please check your connection and try again.");
      setIsProcessing(false);
      return;
    } finally {
      submittingRef.current = false;
    }
    if (!paymentMethodId) {
      setIsProcessing(false);
      return;
    }
    const pendingCharge: PendingCharge = {
      paymentMethodId,
      amount,
      wasTrialing: isTrialing,
      idempotencyKey: attempt.resolve({
        userId: user.id,
        amount,
        paymentMethodId,
        walletType: wallet?.type ?? (walletMethod?.paymentMethodId === paymentMethodId ? walletMethod.type : undefined)
      }),
      analytics
    };
    setCharge(pendingCharge);
    void performCharge(pendingCharge);
  };
  const submit: FormEventHandler<HTMLFormElement> = async e => {
    e.preventDefault();
    if (!isWalletOpen) await purchase();
  };

  /** An exhausted poll leaves the charge outcome unknown: it may still settle, so the attempt key and confirmed card survive for a replay-safe retry while the in-flight form state is released. */
  const releaseChargeKeepingAttempt = useCallback(() => {
    setCharge(null);
    setIsProcessing(false);
    setError("Your payment is still processing. There is no need to pay again. Your balance will update once it completes.");
  }, []);
  const finalizeFailure = useCallback((message: string, userAction?: string | null) => {
    setCharge(null);
    setIsProcessing(false);
    setError(message);
    setErrorAction(userAction ?? null);
    if (confirmedNewCardRef.current) {
      // confirmSetup already saved the card even though the charge failed; surface it in the saved-methods list.
      refreshPaymentMethods();
    }
  }, [refreshPaymentMethods]);
  const moveOffRefusedMethod = useCallback(() => {
    finalizeFailure(BANK_REFUSED_MESSAGE);
    leadWithNewPaymentMethod();
  }, [finalizeFailure, leadWithNewPaymentMethod]);
  const failRefusedCharge = useCallback((paymentMethodId: string, message: string, userAction?: string | null) => {
    if (refusals.record(paymentMethodId) >= CHARGE_DECLINES_BEFORE_RESET) {
      moveOffRefusedMethod();
    } else {
      finalizeFailure(message, userAction);
    }
  }, [refusals, moveOffRefusedMethod, finalizeFailure]);
  const trackThreeDSecureCompleted = (result: ThreeDSecureResult) => {
    analyticsService.track("add_credits_3ds_completed", {
      category: "billing",
      ...threeDSecureChargeRef.current?.analytics,
      result
    });
  };
  const threeDSecure = d.use3DSecure({
    onSuccess: function onThreeDSecureSuccess() {
      trackThreeDSecureCompleted("succeeded");
      pollForPayment();
    },
    onError: function onThreeDSecureError(message, isDecline) {
      const pending = threeDSecureChargeRef.current;
      trackThreeDSecureCompleted("failed");
      trackPaymentFailed(pending?.analytics ?? null, "three_d_secure");
      if (pending && isDecline) {
        failRefusedCharge(pending.paymentMethodId, message);
      } else {
        finalizeFailure(message);
      }
    },
    showSuccessMessage: false,
    showErrorMessage: false
  });

  /** A replayed attempt the webhook already credited settles polling against the pre-charge baseline: the balance rose before polling started, so the default start-of-poll snapshot would never register an increase and the flow would time out. */
  const pollForAlreadyCreditedPayment = useCallback((chargedAmount: number) => {
    pollForPayment({
      initialBalance: balance ? balance.totalUsd - chargedAmount : null
    });
  }, [pollForPayment, balance]);
  const performCharge = useCallback(async function performCharge(pending: PendingCharge) {
    if (!user?.id) return;
    try {
      const chargeResult = await confirmPayment({
        userId: user.id,
        paymentMethodId: pending.paymentMethodId,
        amount: pending.amount,
        idempotencyKey: pending.idempotencyKey
      });
      setCharge(current => current && {
        ...current,
        transactionId: chargeResult.transactionId
      });
      if (chargeResult.requiresAction && chargeResult.clientSecret && chargeResult.paymentIntentId) {
        threeDSecureChargeRef.current = pending;
        analyticsService.track("add_credits_3ds_required", {
          category: "billing",
          ...pending.analytics
        });
        threeDSecure.start3DSecure({
          clientSecret: chargeResult.clientSecret,
          paymentIntentId: chargeResult.paymentIntentId,
          paymentMethodId: pending.paymentMethodId
        });
        return;
      }
      if (chargeResult.success) {
        if (chargeResult.transactionStatus === "succeeded") {
          pollForAlreadyCreditedPayment(pending.amount);
        } else {
          pollForPayment();
        }
        return;
      }
      trackPaymentFailed(pending.analytics, "charge_declined");
      failRefusedCharge(pending.paymentMethodId, "Payment failed. Please try again.");
    } catch (err) {
      const details = toPaymentFailureDetails(err);
      if (details.errorCode === PAYMENT_IN_PROGRESS_CODE) {
        pollForPayment();
        return;
      }
      attempt.clearIfConcluded(err);
      trackPaymentFailed(pending.analytics, "charge_error", details);
      if (isBankRefusal(details)) {
        refusals.record(pending.paymentMethodId);
        moveOffRefusedMethod();
        return;
      }
      const stripeError = d.handleStripeError(err);
      if (details.httpStatus === 402) {
        failRefusedCharge(pending.paymentMethodId, stripeError.message, stripeError.userAction);
      } else {
        finalizeFailure(stripeError.message, stripeError.userAction);
      }
    }
  }, [user?.id, confirmPayment, threeDSecure, pollForPayment, pollForAlreadyCreditedPayment, finalizeFailure, failRefusedCharge, moveOffRefusedMethod, refusals, trackPaymentFailed, analyticsService, attempt, d]);
  useEffect(function completeWhenPollingSettles() {
    const wasPolling = wasPollingRef.current;
    wasPollingRef.current = isPolling;
    if (!wasPolling || isPolling || !charge) return;
    if (lastOutcome !== "success") {
      trackPaymentFailed(charge.analytics, "confirmation_timeout");
      releaseChargeKeepingAttempt();
      return;
    }
    if (charge.wasTrialing && isTrialing) {
      trackPaymentFailed(charge.analytics, "trial_activation_timeout");
      finalizeFailure("Payment did not complete in time. Please try again.");
      return;
    }
    confirmedNewCardRef.current = null;
    attempt.clear();
    refusals.clear();
    setCharge(null);
    analyticsService.track("add_credits_purchased", {
      category: "billing",
      ...charge.analytics
    });
    void queryClient.invalidateQueries({
      queryKey: api.v1.getFirstPurchaseBonus.getKey()
    });
    const {
      amount,
      transactionId
    } = charge;
    void async function completeOnceThePaymentSettles() {
      const settledTransaction = transactionId ? await d.waitForSettledTransaction(stripe, transactionId) : undefined;
      void queryClient.invalidateQueries({
        queryKey: api.v1.listStripeTransactions.getKey()
      });
      onDone(amount, (settledTransaction?.bonusAmount ?? 0) / 100);
      setIsProcessing(false);
    }();
  }, [isPolling, lastOutcome, isTrialing, charge, releaseChargeKeepingAttempt, finalizeFailure, trackPaymentFailed, attempt, refusals, onDone, stripe, analyticsService, api, queryClient, d]);
  useEffect(function reportProcessing() {
    onProcessingChange?.(isBusy);
  }, [isBusy, onProcessingChange]);
  const changeSelectedMethod = useCallback((methodId: string) => {
    if (methodId === NEW_CARD) {
      lastPaymentTypeRef.current = null;
      setPaymentFormLoad(restartPaymentFormLoad);
      if (confirmedNewCardRef.current) {
        confirmedNewCardRef.current = null;
        resetSetupIntent();
      }
    }
    setSelectedMethodId(methodId);
    const method = methodId === NEW_CARD ? undefined : paymentMethods?.find(candidate => candidate.id === methodId);
    if (method) {
      analyticsService.track("add_credits_payment_method_selected", {
        category: "billing",
        type: toPaymentMethodType(method.type)
      });
    }
  }, [resetSetupIntent, paymentMethods, analyticsService]);
  const trackAmountSelected = useCallback((amount: number, isCustom: boolean) => {
    if (amount > 0) {
      analyticsService.track("add_credits_amount_selected", {
        category: "billing",
        amount,
        isCustom
      });
    }
  }, [analyticsService]);
  const markPaymentFormReady = useCallback(() => {
    clearTimeout(paymentFormLoadTimeoutRef.current);
    setPaymentFormLoad(load => ({
      ...load,
      status: "ready"
    }));
    if (paymentFormReadyTrackedRef.current) return;
    paymentFormReadyTrackedRef.current = true;
    analyticsService.track("add_credits_payment_form_ready", {
      category: "billing",
      type: lastPaymentTypeRef.current ?? "card",
      msSinceOpen: Math.round(performance.now() - openedAt),
      context
    });
  }, [analyticsService, openedAt, context]);
  const failPaymentFormLoad = useCallback((reason: PaymentFormLoadFailure) => {
    setPaymentFormLoad(load => ({
      ...load,
      status: reason === "timeout" ? "slow" : "failed"
    }));
    analyticsService.track("add_credits_payment_form_load_failed", {
      category: "billing",
      reason,
      msSinceOpen: Math.round(performance.now() - openedAt),
      context
    });
  }, [analyticsService, openedAt, context]);
  useEffect(function flagASlowPaymentFormAfterTimeout() {
    if (!isPaymentFormLoading) return;
    const timeout = setTimeout(() => failPaymentFormLoad("timeout"), PAYMENT_FORM_LOAD_TIMEOUT_MS);
    paymentFormLoadTimeoutRef.current = timeout;
    return () => clearTimeout(timeout);
  }, [isPaymentFormLoading, failPaymentFormLoad]);
  useEffect(function reloadACardFormThatFailedBeforeItsSetupIntentArrived() {
    setPaymentFormLoad(load => load.status === "failed" ? restartPaymentFormLoad(load) : load);
  }, [setupIntent?.clientSecret]);
  const trackPaymentTypeSelected = useCallback((type: string) => {
    const normalizedType = toPaymentMethodType(type);
    if (lastPaymentTypeRef.current === normalizedType) {
      return;
    }
    lastPaymentTypeRef.current = normalizedType;
    analyticsService.track("add_credits_payment_method_selected", {
      category: "billing",
      type: normalizedType
    });
  }, [analyticsService]);
  return <>
      <d.FirstPurchaseBonusAlert amount={amount} data-sentry-element="d.FirstPurchaseBonusAlert" data-sentry-source-file="AddCreditsForm.tsx" />

      <form className="space-y-6" onSubmit={submit}>
        <fieldset disabled={isBusy} className="space-y-6">
          <d.AddCreditsAmountFields value={amountInput} onChange={setAmountInput} minAmount={topUpMinAmountUsd} error={amountError} onAmountCommit={trackAmountSelected} deploymentDailyCostUsd={deploymentDailyCostUsd} data-sentry-element="d.AddCreditsAmountFields" data-sentry-source-file="AddCreditsForm.tsx" />

          <d.AddCreditsWalletButton amount={amount} disabled={isBusy || isPaused || amount < topUpMinAmountUsd || isLoadingMethods || !!pendingWallet} onSubmit={purchase} onOpenChange={setIsWalletOpen} data-sentry-element="d.AddCreditsWalletButton" data-sentry-source-file="AddCreditsForm.tsx" />

          {pendingWallet && !isBusy && <p className="text-sm text-muted-foreground">
              Your wallet payment is awaiting confirmation. Retry the saved wallet below to check it without starting another charge.
            </p>}

          {isMethodsError && <Alert variant="destructive">
              <AlertDescription>We couldn&apos;t load your saved payment methods. You can still pay with a new card.</AlertDescription>
            </Alert>}

          {isLoadingMethods ? <d.Skeleton className="h-10 w-full" /> : (!!paymentMethods?.length || !!walletMethod) && <div className="space-y-2">
                <d.Label htmlFor="add-credits-payment-method">Payment method</d.Label>
                <d.Select value={selectedMethodId ?? ""} onValueChange={changeSelectedMethod} disabled={isBusy}>
                  <d.SelectTrigger id="add-credits-payment-method">
                    <d.SelectValue placeholder="Select a payment method" />
                  </d.SelectTrigger>
                  <d.SelectContent>
                    {paymentMethods?.map(method => <d.SelectItem key={method.id} value={method.id}>
                        {d.getPaymentMethodDisplay(method).label}
                      </d.SelectItem>)}
                    {walletMethod && !paymentMethods?.some(method => method.id === walletMethod.paymentMethodId) && <d.SelectItem value={walletMethod.paymentMethodId}>
                        {walletMethod.type === "apple_pay" ? "Apple Pay" : walletMethod.type === "google_pay" ? "Google Pay" : "Wallet"}
                      </d.SelectItem>}
                    <d.SelectItem value={NEW_CARD}>Add new payment method</d.SelectItem>
                  </d.SelectContent>
                </d.Select>
              </div>}

          {isNewCard && setupIntentStatus === "error" && !hasPaymentFormFailed && <Alert variant="destructive">
              <AlertDescription>We couldn&apos;t load the payment form. Please try again.</AlertDescription>
              <Button type="button" variant="outline" onClick={() => createSetupIntent()} className="mt-2">
                Retry
              </Button>
            </Alert>}

          {hasPaymentFormFailed && <Alert variant="destructive">
              <AlertDescription>The card form didn&apos;t load</AlertDescription>
              <Button type="button" variant="outline" onClick={leadWithNewPaymentMethod} className="mt-2">
                Retry
              </Button>
            </Alert>}

          {isPaymentFormSlow && <Alert variant="warning">
              <AlertDescription>The card form is taking longer than usual. You can keep waiting for it below, or retry.</AlertDescription>
              <Button type="button" variant="outline" onClick={leadWithNewPaymentMethod} className="mt-2">
                Retry
              </Button>
            </Alert>}

          {isNewCard && !hasPaymentFormFailed && <d.AddCreditsNewPaymentMethodFields key={paymentFormLoad.attempt} ref={paymentMethodRef} clientSecret={setupIntent?.clientSecret} isLoading={isSetupLoading} disabled={isBusy || !!confirmedNewCardRef.current} onPaymentTypeChange={trackPaymentTypeSelected} onReady={markPaymentFormReady} onLoadFailed={failPaymentFormLoad} />}

          {isNewCard && confirmedNewCardRef.current && !isBusy && <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Your payment method is saved for this purchase. You can retry with it or use a different payment method.
              </p>
              <Button type="button" variant="outline" onClick={() => changeSelectedMethod(NEW_CARD)}>
                Use a different payment method
              </Button>
            </div>}

          {error && <Alert variant="destructive">
              <AlertDescription>
                <span className="block font-medium">{error}</span>
                {errorAction && <span className="mt-1 block text-sm">{errorAction}</span>}
              </AlertDescription>
            </Alert>}

          {refusals.pausedUntil !== null && <Alert variant="destructive">
              <AlertDescription>{describePause(refusals.pausedUntil)}</AlertDescription>
            </Alert>}

          <LoadingButton type="submit" className="w-full" loading={isProcessing} disabled={isBusy || isPaused || amount < topUpMinAmountUsd || isLoadingMethods || isNewCard && paymentFormLoad.status !== "ready"} data-sentry-element="LoadingButton" data-sentry-source-file="AddCreditsForm.tsx">
            {isPaymentFormLoading || isPaymentFormSlow ? "Loading card form…" : "Purchase Credits"}
          </LoadingButton>
        </fieldset>
      </form>

      {threeDSecure.threeDSData?.clientSecret && <d.ThreeDSecurePopup isOpen={threeDSecure.isOpen} onSuccess={threeDSecure.handle3DSSuccess} onError={threeDSecure.handle3DSError} clientSecret={threeDSecure.threeDSData.clientSecret} paymentIntentId={threeDSecure.threeDSData.paymentIntentId} paymentMethodId={threeDSecure.threeDSData.paymentMethodId} title="Payment Authentication" description="Your bank requires additional verification for this payment." successMessage="Payment authenticated successfully!" errorMessage="Please try again or use a different payment method." />}
    </>;
}
function restartPaymentFormLoad({
  attempt
}: PaymentFormLoad): PaymentFormLoad {
  return {
    attempt: attempt + 1,
    status: "loading"
  };
}
function isBankRefusal(failure: PaymentMethodSetupFailure): boolean {
  return failure.errorCode === BANK_REFUSAL_CODE;
}

/** Only the card or its 3D Secure check refused; an incomplete form, a rate limit or a stale setup is not the payment method's fault. */
function isRefusedByStripe(failure: PaymentMethodSetupFailure): boolean {
  return failure.errorType === CARD_ERROR_TYPE || failure.errorCode === SETUP_AUTHENTICATION_FAILURE_CODE;
}
function describePause(pausedUntil: number): string {
  const minutes = Math.max(1, Math.ceil((pausedUntil - Date.now()) / MS_PER_MINUTE));
  return `Too many payments were refused in a short time. You can try again in ${minutes} ${minutes === 1 ? "minute" : "minutes"}.`;
}
function toPaymentFailureDetails(error: unknown): PaymentFailureDetails {
  const {
    response
  } = (error ?? {}) as {
    response?: {
      status?: number;
      data?: {
        code?: string;
      };
    };
  };
  return {
    errorCode: response?.data?.code,
    httpStatus: response?.status
  };
}

/** Maps a Stripe payment-method type (`card`, `us_bank_account`, …) to the coarse label reported to analytics. */
function toPaymentMethodType(rawType: string): string {
  if (rawType === "card") return "card";
  if (rawType.includes("bank")) return "bank";
  return rawType;
}